import { injectable } from 'inversify';
import type {
  AdaptInboundAudioStatsPayload,
  AdaptInboundVideoStatsPayload,
  AdaptOutboundAudioStatsPayload,
  AdaptOutboundVideoStatsPayload,
  AudioStreamStats,
  PacketLossStats,
  VideoStreamStats,
} from './types';
import { MS_IN_SEC } from './constants';
import { clamp } from './helpers';
import { getLocalStorageItem } from '../engine/analyticsApiClient/helper';

const PERCENTAGE_MULTIPLIER = 100;
const DEFAULT_RTT = 50;

@injectable()
export class WIDStatsAdapter {
  adaptInboundAudioStats(payload: AdaptInboundAudioStatsPayload): AudioStreamStats {
    const { stats, connectionStats, fec, dtx, prevStats } = payload;
    const packetLoss = this.calculateDeltaPacketLoss(
      { packetsLost: stats.packetsLost, packetsReceivedOrSent: stats.packetsReceived },
      prevStats ? { packetsLost: prevStats.packetsLost, packetsReceivedOrSent: prevStats.packetsReceived } : undefined,
    );

    const bufferDelay = this.calculateJitterBufferDelay(
      stats.jitterBufferDelay,
      stats.jitterBufferEmittedCount,
    );

    return {
      ssrc: stats.ssrc,
      dtx,
      fec,
      packetLoss,
      bufferDelay,
      bitrate: stats.bitrate,
      roundTripTime: connectionStats.currentRoundTripTime * MS_IN_SEC,
    };
  }

  adaptInboundVideoStats(payload: AdaptInboundVideoStatsPayload): VideoStreamStats {
    const { stats, connectionStats, prevStats } = payload;
    const packetLoss = this.calculateDeltaPacketLoss(
      { packetsLost: stats.packetsLost, packetsReceivedOrSent: stats.packetsReceived },
      prevStats ? { packetsLost: prevStats.packetsLost, packetsReceivedOrSent: prevStats.packetsReceived } : undefined,
    );

    const bufferDelay = this.calculateJitterBufferDelay(
      stats.jitterBufferDelay,
      stats.jitterBufferEmittedCount,
    );

    // qpSum is available at runtime but not typed in webrtc-issue-detector for inbound stats
    const inboundWithQP = stats as { qpSum?: number; framesDecoded: number };
    const prevWithQP = prevStats as { qpSum?: number; framesDecoded: number } | undefined;
    const averageQP = this.calculateDeltaAverageQP(
      inboundWithQP.qpSum, inboundWithQP.framesDecoded,
      prevWithQP?.qpSum, prevWithQP?.framesDecoded,
    );

    return {
      ssrc: stats.ssrc,
      packetLoss,
      bufferDelay,
      bitrate: stats.bitrate,
      roundTripTime: connectionStats.currentRoundTripTime * MS_IN_SEC,
      width: stats.frameWidth,
      height: stats.frameHeight,
      expectedWidth: payload.expectedWidth ?? stats.frameWidth,
      expectedHeight: payload.expectedHeight ?? stats.frameHeight,
      frameRate: stats.framesPerSecond,
      expectedFrameRate: payload.expectedFrameRate ?? stats.framesPerSecond,
      isStaticContent: payload.isStaticContent,
      averageQP,
    };
  }

  adaptOutboundAudioStats(payload: AdaptOutboundAudioStatsPayload): AudioStreamStats {
    const { stats, remoteInboundStats, fec, dtx, prevStats } = payload;
    const packetLoss = remoteInboundStats
      ? this.calculateDeltaPacketLoss(
        { packetsLost: remoteInboundStats.packetsLost, packetsReceivedOrSent: stats.packetsSent },
        prevStats
          ? { packetsLost: prevStats.remoteInbound.packetsLost, packetsReceivedOrSent: prevStats.outbound.packetsSent }
          : undefined,
      )
      : 0;

    const rtt = remoteInboundStats?.roundTripTime ? remoteInboundStats.roundTripTime * MS_IN_SEC : DEFAULT_RTT;
    const bufferDelay = remoteInboundStats?.jitter ? remoteInboundStats.jitter * MS_IN_SEC : 0;

    return {
      ssrc: stats.ssrc,
      fec,
      dtx,
      packetLoss,
      bufferDelay,
      roundTripTime: rtt,
      bitrate: stats.bitrate,
    };
  }

  adaptOutboundVideoStats(payload: AdaptOutboundVideoStatsPayload): VideoStreamStats {
    const { stats, remoteInboundStats, prevStats } = payload;
    const packetLoss = remoteInboundStats
      ? this.calculateDeltaPacketLoss(
        { packetsLost: remoteInboundStats.packetsLost, packetsReceivedOrSent: stats.packetsSent },
        prevStats
          ? { packetsLost: prevStats.remoteInbound.packetsLost, packetsReceivedOrSent: prevStats.outbound.packetsSent }
          : undefined,
      )
      : 0;

    const rtt = remoteInboundStats?.roundTripTime  ? remoteInboundStats.roundTripTime * MS_IN_SEC : DEFAULT_RTT;
    const bufferDelay = remoteInboundStats?.jitter ? remoteInboundStats.jitter * MS_IN_SEC : 0;

    const averageQP = this.calculateDeltaAverageQP(
      stats.qpSum, stats.framesEncoded,
      prevStats?.outbound.qpSum, prevStats?.outbound.framesEncoded,
    );

    return {
      ssrc: stats.ssrc,
      packetLoss,
      bufferDelay,
      bitrate: stats.bitrate,
      roundTripTime: rtt,
      width: stats.frameWidth,
      height: stats.frameHeight,
      expectedWidth: stats.frameWidth ?? stats.track?.width,
      expectedHeight: stats.frameHeight ?? stats.track?.height,
      frameRate: stats.framesPerSecond,
      expectedFrameRate: stats.framesPerSecond ?? stats.track?.framesPerSecond,
      decreaseOutboundPenalty: getLocalStorageItem('qosDecreaseOutboundPenalty') ?? true,
      isStaticContent: payload.isStaticContent,
      averageQP,
      qualityLimitationReason: stats.qualityLimitationReason,
    };
  }

  private calculateDeltaPacketLoss(
    stats: PacketLossStats,
    prevStats?: PacketLossStats,
  ): number {
    const packetsLost = clamp(stats.packetsLost - (prevStats?.packetsLost ?? 0), 0, stats.packetsLost);
    const packetsReceivedOrSent = clamp(
      stats.packetsReceivedOrSent - (prevStats?.packetsReceivedOrSent ?? 0),
      0,
      stats.packetsReceivedOrSent,
    );

    return packetsReceivedOrSent || packetsLost
      ? (packetsLost / (packetsLost + packetsReceivedOrSent)) * PERCENTAGE_MULTIPLIER
      : 0;
  }

  private calculateDeltaAverageQP(
    qpSum?: number,
    frameCount?: number,
    prevQpSum?: number,
    prevFrameCount?: number,
  ): number | undefined {
    if (qpSum === undefined || frameCount === undefined) {
      return undefined;
    }

    const deltaFrames = frameCount - (prevFrameCount ?? 0);
    const deltaQpSum = qpSum - (prevQpSum ?? 0);

    if (deltaFrames <= 0) {
      return undefined;
    }

    return deltaQpSum / deltaFrames;
  }

  private calculateJitterBufferDelay(
    jitterBufferDelay: number,
    jitterBufferEmittedCount: number,
  ): number {
    if (jitterBufferEmittedCount === 0) {
      return 0;
    }

    return (jitterBufferDelay / jitterBufferEmittedCount) * MS_IN_SEC;
  }
}
